import { forwardRef, useMemo } from 'react'
import { EChart, type EChartHandle } from './EChart'
import { buildHeatmapOption, heatmapHeight, type HeatmapInput } from './heatmapOption'
import { useChartTheme } from './useChartTheme'

export interface HeatmapGridProps extends HeatmapInput {
  height?: number
  loading?: boolean
  ariaLabel?: string
  className?: string
  texture?: boolean
  /** Клик по ячейке — обычно панель детализации. */
  onCellClick?: (rowIndex: number, columnIndex: number) => void
  /** Клик по подписи строки — обычно провал по объекту. */
  onRowClick?: (rowIndex: number) => void
}

/**
 * Матрица «строки × колонки»: «объект × неделя» (расходящаяся шкала Δ%), «день недели × час»
 * (последовательная), «класс было × стало». Легенду шкалы показывайте рядом (`ScaleLegend`).
 */
export const HeatmapGrid = forwardRef<EChartHandle, HeatmapGridProps>(function HeatmapGrid(
  { height, loading, ariaLabel, className, texture, onCellClick, onRowClick, ...input },
  ref,
) {
  const theme = useChartTheme()
  const option = useMemo(
    () => buildHeatmapOption(input, theme),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      theme,
      input.rows,
      input.columns,
      input.values,
      input.colorValues,
      input.scale,
      input.divergingLimit,
      input.valueFormat,
      input.colorFormat,
      input.colorLabel,
      input.cellLabels,
      input.rowLabelWidth,
      input.cellFormat,
      input.showColorInTooltip,
      input.tooltipExtra,
      input.cellBorders,
    ],
  )

  return (
    <EChart
      ref={ref}
      option={option}
      height={height ?? heatmapHeight(input.rows.length)}
      loading={loading}
      ariaLabel={ariaLabel}
      className={className}
      texture={texture}
      onMarkClick={(params) => {
        if (params.componentType === 'yAxis') {
          const key = String(params.value ?? '')
          const rowIndex = Number(key.slice(1))
          if (key.startsWith('r') && Number.isInteger(rowIndex)) onRowClick?.(rowIndex)
          return
        }
        if (params.componentType !== 'series') return
        const value = (params.data?.value ?? params.value) as unknown
        if (!Array.isArray(value)) return
        const [columnIndex, rowIndex] = value as number[]
        if (Number.isInteger(rowIndex) && Number.isInteger(columnIndex)) onCellClick?.(rowIndex, columnIndex)
      }}
    />
  )
})

export default HeatmapGrid
